import { CheckIcon, ChevronDownIcon, Loader2Icon, LogOutIcon, RefreshCwIcon } from "lucide-react";

import { Button } from "../ui/button";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "../ui/menu";
import {
  pluginConnectionState,
  pluginStateLabel,
  type ForgePlugin,
  type ForgePluginId,
  type PluginConnectionAttempt,
  type PluginConnectionState,
} from "./pluginsPage.logic";
import type { PluginConnectionCapabilities } from "./pluginConnections";

function pluginStateDotClassName(state: PluginConnectionState): string {
  switch (state) {
    case "connected":
      return "bg-success";
    case "error":
      return "bg-warning";
    case "pending":
    case "unknown":
      return "bg-muted-foreground/50";
  }
}

function pluginConnectButtonLabel(input: {
  readonly state: PluginConnectionState;
  readonly attempt: PluginConnectionAttempt | null;
  readonly pluginId: ForgePluginId;
}): string {
  if (input.attempt?.pluginId === input.pluginId) {
    return input.attempt.status === "opening" ? "Opening..." : "Waiting...";
  }
  if (input.state === "error") return "Reconnect";
  return "Connect";
}

interface PluginStateLabelProps {
  readonly state: PluginConnectionState;
}

export function PluginStateLabel({ state }: PluginStateLabelProps) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      <span
        className={`size-1.5 rounded-full ${pluginStateDotClassName(state)}`}
        aria-hidden="true"
      />
      {pluginStateLabel(state)}
    </span>
  );
}

interface PluginConnectionActionProps {
  readonly plugin: ForgePlugin & { readonly capabilities?: PluginConnectionCapabilities };
  readonly attempt: PluginConnectionAttempt | null;
  readonly onConnect: (pluginId: ForgePluginId) => void;
  readonly onDisconnect?: ((pluginId: ForgePluginId) => void) | undefined;
  // While the initial connection status is still loading, show a neutral
  // placeholder instead of Connect/Disconnect — otherwise a connected plugin
  // briefly flashes "Connect" before the status resolves.
  readonly isChecking?: boolean | undefined;
}

export function PluginConnectionAction({
  plugin,
  attempt,
  onConnect,
  onDisconnect,
  isChecking,
}: PluginConnectionActionProps) {
  const state = pluginConnectionState(plugin);
  const isConnecting = attempt?.pluginId === plugin.id;
  const canConnect = state !== "connected";
  const connectAvailable = plugin.capabilities?.connect ?? true;

  if (!isChecking && canConnect && !connectAvailable) {
    return (
      <Button type="button" size="sm" variant="secondary" disabled>
        Unavailable
      </Button>
    );
  }

  return isChecking ? (
    <Button
      type="button"
      size="sm"
      variant="secondary"
      disabled
      aria-label={`Checking ${plugin.name} connection`}
    >
      <Loader2Icon className="size-4 animate-spin" />
      Checking...
    </Button>
  ) : canConnect ? (
    <Button
      type="button"
      size="sm"
      variant="secondary"
      aria-label={`${pluginConnectButtonLabel({ state, attempt, pluginId: plugin.id })} ${plugin.name}`}
      disabled={isConnecting}
      onClick={() => onConnect(plugin.id)}
    >
      {pluginConnectButtonLabel({ state, attempt, pluginId: plugin.id })}
    </Button>
  ) : (
    <Menu>
      <MenuTrigger className="inline-flex h-8 items-center gap-2 rounded-lg border border-border bg-popover px-3 text-sm text-foreground shadow-xs/5 outline-none transition-colors hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring">
        <span className="size-1.5 rounded-full bg-success" aria-hidden="true" />
        {plugin.accountLabel ? plugin.accountLabel : "Connected"}
        <ChevronDownIcon className="size-3.5 text-muted-foreground" />
      </MenuTrigger>
      <MenuPopup align="end" side="bottom">
        <MenuItem onClick={() => onConnect(plugin.id)}>
          <RefreshCwIcon className="size-4" />
          Reconnect
        </MenuItem>
        {onDisconnect ? (
          <MenuItem onClick={() => onDisconnect(plugin.id)}>
            <LogOutIcon className="size-4" />
            Disconnect
          </MenuItem>
        ) : (
          <MenuItem disabled>
            <CheckIcon className="size-4" />
            Connected
          </MenuItem>
        )}
      </MenuPopup>
    </Menu>
  );
}
